/* ============================================================
   Fills — UI compartida.
   <fills-carousel>: carrusel por scroll-snap (CSS hace el trabajo;
   JS solo agrega dots/flechas y estado). Sin dependencias.
   <fills-lightbox-open>: abre un <dialog> nativo (galería/video).
   ============================================================ */

if (!customElements.get('fills-carousel')) {
  customElements.define(
    'fills-carousel',
    class FillsCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.setAttribute('role', 'group');
        this.setAttribute('aria-roledescription', 'carrusel');
        this.slides = Array.from(this.track.children);
        // 1 solo slide (p.ej. hero en preventa): NO es carrusel. En iOS un scroll-snap
        // container reclama el gesto vertical aunque no haya a dónde desplazarse, así que
        // lo neutralizamos por completo para que el scroll de la página pase libre.
        if (this.slides.length <= 1) {
          this.track.style.overflowX = 'visible';
          this.track.style.scrollSnapType = 'none';
          this.track.style.touchAction = 'auto';
          this.track.style.webkitOverflowScrolling = 'auto';
        }
        this.dotsWrap = this.querySelector('[data-dots]');
        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');

        if (this.dotsWrap && this.slides.length > 1) {
          this.slides.forEach((_, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'fills-car__dot';
            b.setAttribute('aria-label', 'Ir al elemento ' + (i + 1));
            b.addEventListener('click', () => this.goTo(i));
            this.dotsWrap.appendChild(b);
          });
          this.dots = Array.from(this.dotsWrap.children);
        }
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

        this.track.addEventListener('scroll', () => {
          clearTimeout(this._t);
          this._t = setTimeout(() => this.sync(), 80);
        }, { passive: true });

        // Autoplay opcional (data-autoplay="ms"): avanza solo, se DETIENE para
        // siempre a la primera interacción del usuario y respeta reduced-motion.
        const auto = parseInt(this.getAttribute('data-autoplay'), 10);
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (auto > 0 && this.slides.length > 1 && !reduce) {
          const stop = () => { clearInterval(this._auto); this._auto = null; };
          this._auto = setInterval(() => {
            if (document.hidden) return;
            this.goTo((this.current() + 1) % this.slides.length);
          }, auto);
          ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((ev) =>
            this.addEventListener(ev, stop, { passive: true, once: true }));
        }
        this.sync();
      }
      current() {
        const x = this.track.scrollLeft;
        let best = 0, dist = Infinity;
        this.slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - x);
          if (d < dist) { dist = d; best = i; }
        });
        return best;
      }
      goTo(i) {
        const s = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
        if (s) this.track.scrollTo({ left: s.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
      step(dir) { this.goTo(this.current() + dir); }
      sync() {
        const c = this.current();
        if (this.dots) this.dots.forEach((d, i) => {
          const on = i === c;
          d.classList.toggle('is-active', on);
          if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
        if (this.prevBtn) this.prevBtn.disabled = c === 0;
        if (this.nextBtn) this.nextBtn.disabled = c >= this.slides.length - 1;
      }
    }
  );
}

/* Lightbox: cualquier [data-fills-lightbox] abre el dialog del section,
   inyectando imagen o video según data-src / data-video. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-fills-lightbox]');
  if (!btn) return;
  const dialog = btn.closest('section, .shopify-section, body').querySelector('dialog[data-fills-dialog]');
  if (!dialog) return;
  const slot = dialog.querySelector('[data-dialog-slot]');
  if (slot) {
    if (btn.dataset.video) {
      slot.innerHTML = '<video src="' + btn.dataset.video + '" controls autoplay playsinline style="width:100%;height:auto;border-radius:12px;"></video>';
    } else if (btn.dataset.src) {
      slot.innerHTML = '<img src="' + btn.dataset.src + '" alt="' + (btn.dataset.alt || '') + '" style="width:100%;height:auto;border-radius:12px;">';
    }
  }
  dialog.showModal();
});
document.addEventListener('click', (e) => {
  const d = e.target.closest('dialog[data-fills-dialog]');
  if (!d) return;
  // clic fuera del contenido o en el botón de cerrar
  if (e.target === d || e.target.closest('[data-dialog-close]')) {
    d.close();
    const slot = d.querySelector('[data-dialog-slot]');
    if (slot) slot.innerHTML = '';
  }
});

/* ============================================================
   Video lazy: flavor cards (.fills-fc2__video, src vía data-src) y la
   sección de beneficios (.fills-sci__video, src vía <source>).
   PERFORMANCE: solo se reproducen (y se baja el video) cuando entran en
   viewport (IntersectionObserver); pausa al salir. En las flavor cards el
   <video> no tiene src hasta entonces (cero bytes); el poster es el <img>/
   primer frame, LCP intacto. Respeta prefers-reduced-motion y Save-Data
   (no reproduce; deja el poster/primer frame).
   ============================================================ */
(function () {
  var vids = document.querySelectorAll('.fills-fc2__video[data-src], .fills-sci__video');
  if (!vids.length || !('IntersectionObserver' in window)) return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || {};
  if (reduce || conn.saveData) return; // se queda el poster, sin descargar video
  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (v.dataset.src && !v.getAttribute('src')) v.setAttribute('src', v.dataset.src);
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    },
    { rootMargin: '200px 0px', threshold: 0.25 }
  );
  vids.forEach(function (v) {
    v.addEventListener('playing', function () { v.classList.add('is-playing'); });
    io.observe(v);
  });
})();

/* ============================================================
   Registro "Avísame cuando vuelva" FUERA del buybox (footer marca, bandas):
   mismo pipe Klaviyo client API que fills-buybox.js. Solo binda forms que NO
   viven dentro de <fills-buybox> (ese ya tiene el suyo con sabor de interés).
   ============================================================ */
(function () {
  document.querySelectorAll('[data-notify-form]').forEach(function (form) {
    if (form.closest('fills-buybox')) return;
    var company = form.dataset.klCompany;
    var list = form.dataset.klList;
    var wrap = form.parentElement;
    var msg = wrap ? wrap.querySelector('[data-notify-msg]') : null;
    var btn = form.querySelector('[data-notify-submit]');
    function say(text, ok) {
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = false;
      msg.classList.toggle('is-error', !ok);
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = ((form.email && form.email.value) || '').trim();
      if (!email || !company || !list) { say('Escribe un correo válido.', false); return; }
      if (btn) btn.disabled = true;
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Footer sitio',
              profile: { data: { type: 'profile', attributes: { email: email, properties: { Origen: 'Footer sitio' } } } },
            },
            relationships: { list: { data: { type: 'list', id: list } } },
          },
        }),
      }).then(function (res) {
        if (res.ok || res.status === 202) {
          form.hidden = true;
          say(form.dataset.klSuccess || '¡Listo! Te avisamos.', true);
        } else { throw new Error('http ' + res.status); }
      }).catch(function () {
        if (btn) btn.disabled = false;
        say('No pudimos registrarte. Inténtalo de nuevo.', false);
      });
    });
  });
})();

/* ============================================================
   Header: colapso del logo a isotipo al scrollear (robusto).
   Toggle de .scrolled-past-header en .section-header por scroll, sin depender del
   sticky-header de Dawn (que a veces no carga). En el sitio real ambos coinciden.
   rAF-throttled + passive; con histéresis para no parpadear cerca del umbral.
   ============================================================ */
(function () {
  if (window.__fillsLogoScroll) return;
  window.__fillsLogoScroll = true;
  var sh = document.querySelector('.section-header');
  if (!sh) return;
  var ticking = false;
  function update() {
    ticking = false;
    var y = window.pageYOffset || document.documentElement.scrollTop;
    if (y > 48) sh.classList.add('scrolled-past-header');
    else if (y < 12) sh.classList.remove('scrolled-past-header');
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  update();
})();
